import React from 'react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen pt-20 pb-16 px-6" data-testid="refund-policy-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-light tracking-tight text-foreground mb-4">
            Refund & Cancellation Policy
          </h1>
          <p className="text-muted-foreground">Effective Date: March 2025 | Last Updated: March 2025</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-12 space-y-8">
            <p className="text-muted-foreground leading-relaxed">
              This policy sets out the terms under which cancellations, rescheduling, and refunds are managed for sessions and services offered through Psychotherapy with Anvita. Please read this carefully before making a booking. By confirming a booking, you agree to the terms described below.
            </p>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">1. The 24-Hour Cancellation Window</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To respect the time and scheduling commitments of the Practitioner, all cancellations and rescheduling requests must be made at least 24 hours before the scheduled appointment time.
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border rounded-xl">
                  <thead>
                    <tr className="bg-secondary/50">
                      <th className="border border-border p-4 text-left font-medium text-foreground">Notice Given</th>
                      <th className="border border-border p-4 text-left font-medium text-foreground">Cancellation Outcome</th>
                      <th className="border border-border p-4 text-left font-medium text-foreground">Refund</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-4 text-muted-foreground">More than 24 hours</td>
                      <td className="border border-border p-4 text-muted-foreground">Full cancellation or free reschedule</td>
                      <td className="border border-border p-4 text-muted-foreground">Full refund</td>
                    </tr>
                    <tr className="bg-secondary/30">
                      <td className="border border-border p-4 text-muted-foreground">Less than 24 hours or no notice</td>
                      <td className="border border-border p-4 text-muted-foreground">At the Practitioner's discretion</td>
                      <td className="border border-border p-4 text-muted-foreground">At the Practitioner's discretion</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-muted-foreground leading-relaxed mt-4">
                Refunds are processed to the original payment method. Processing time is 5–10 business days for Razorpay (INR) and 3–5 business days for PayPal (USD).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">2. How to Cancel or Reschedule</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To cancel or reschedule an appointment, please contact the Practitioner through one of the following:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Email: <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a></li>
                <li>Through the cancellation or rescheduling option in your booking confirmation email</li>
                <li>Via the website's contact or booking management portal</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Cancellation requests must be made in writing (email or through the booking portal) to be eligible for a refund. The time of your written cancellation request — not the time it is read or acknowledged — determines whether the 24-hour window has been met.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">3. Medical Emergency Exception</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Practitioner may, at her sole discretion, grant a full refund or a complimentary reschedule in cases of a verifiable medical emergency affecting the client or an immediate family member.
              </p>
              
              <h3 className="text-xl font-heading font-medium text-foreground mb-3">What Qualifies:</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>A sudden acute medical condition requiring hospitalisation or emergency care</li>
                <li>An accident requiring immediate medical attention</li>
                <li>A serious medical episode affecting an immediate family member in the client's care</li>
              </ul>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">How to Request:</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Contact the Practitioner as soon as reasonably possible via email</li>
                <li>Briefly describe the nature of the emergency</li>
                <li>Provide supporting documentation where available within 7 days of the missed appointment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">4. Cancellation by the Practitioner</h2>
              
              <h3 className="text-xl font-heading font-medium text-foreground mb-3">4.1 Cancellation with Full Refund</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Practitioner reserves the right to cancel or reschedule a session due to unforeseen personal circumstances, illness, technical failure, or force majeure events. In such cases, you will be notified as early as possible, and a full refund will be issued or an alternate session slot will be offered.
              </p>

              <h3 className="text-xl font-heading font-medium text-foreground mb-3">4.2 Cancellation Without Refund — Protective Grounds</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Practitioner reserves the right to immediately terminate a session or cancel all future bookings without issuing a refund in circumstances including: abusive or threatening behaviour, sexual harassment, stalking or unwanted contact, discriminatory language, misrepresentation or fraud, breach of confidentiality, or criminal activity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">5. Online Sessions — Technical Difficulties</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Client-side issues:</strong> If technical difficulties on the client's end prevent the session, the outcome will be handled at the Practitioner's discretion</li>
                <li><strong>Practitioner-side issues:</strong> If technical difficulties on the Practitioner's end prevent the session, the client will be offered a full reschedule or refund</li>
                <li><strong>Mutual technical failure:</strong> A partial session credit or discounted reschedule will be offered as a goodwill gesture</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                The Practitioner will allow a 10-minute grace window before considering an online session as missed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">6. Payment Gateways and Refund Processing</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border rounded-xl">
                  <thead>
                    <tr className="bg-secondary/50">
                      <th className="border border-border p-4 text-left font-medium text-foreground">Gateway</th>
                      <th className="border border-border p-4 text-left font-medium text-foreground">Currency</th>
                      <th className="border border-border p-4 text-left font-medium text-foreground">Typical Refund Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-4 text-muted-foreground">Razorpay</td>
                      <td className="border border-border p-4 text-muted-foreground">INR (₹)</td>
                      <td className="border border-border p-4 text-muted-foreground">5–10 business days</td>
                    </tr>
                    <tr className="bg-secondary/30">
                      <td className="border border-border p-4 text-muted-foreground">PayPal</td>
                      <td className="border border-border p-4 text-muted-foreground">USD ($)</td>
                      <td className="border border-border p-4 text-muted-foreground">3–5 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Refunds are always returned to the original payment method used at the time of booking.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-medium text-foreground mb-4">7. Dispute Resolution</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have a concern about a cancellation or refund decision, please raise it in writing at <a href="mailto:therapywithanvitasethi@gmail.com" className="text-primary hover:underline">therapywithanvitasethi@gmail.com</a>. We will acknowledge your concern within 3 business days and aim to resolve it within 15 business days. If a resolution cannot be reached, the matter shall be governed by the laws of the Republic of India, with disputes subject to the exclusive jurisdiction of the courts in Bengaluru, Karnataka.
              </p>
            </section>

            <div className="border-t border-border pt-8 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By confirming a booking, you acknowledge that you have read and agree to this Refund and Cancellation Policy.
              </p>
              <p className="text-sm text-muted-foreground text-center mt-4">
                © 2025 Psychotherapy with Anvita. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
